import { useEffect } from 'react';
import {
  ButtonHeadless,
  InputLabel,
  Modal,
  Spin,
} from '@wajeez-web/components/ui';
import { Server } from '@wajeez/wajeez.microservices';
import { SubmitHandler, useForm } from 'react-hook-form';
import {
  FlowSelectionsActions,
  FlowsActions,
  SelectionFlowInput,
  getLanguagesLookups,
  useContentDispatch,
  useContentSelector,
} from '@wajeez-web/stores/content';
import {
  useProductDispatch,
  useProductSelector,
  PlansActions,
} from '@wajeez-web/stores/product';
import {
  FormInputController,
  WajeezFormInputV2,
} from '@wajeez-web/components/form-builder';
import { Autocomplete } from '@wajeez-web/components/mui';
import { ReturnTranslatedText } from '@wajeez-web/core/localization';
import { BiLoaderAlt } from 'react-icons/bi';
import { Link, useNavigate } from 'react-router-dom';
import { FiEdit2 } from 'react-icons/fi';
import { useGetLanguagesLookups } from '@wajeez-web/components/helpers';

interface PreviewFlowSelectionModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit?: () => void;
}

type FormInput = Partial<SelectionFlowInput>;

const formInputInitialValues: FormInput = {
  groupState: undefined,
  planIds: undefined,
  platform: undefined,
  iSOThreeDigitCountryCode: undefined,
  appVersionMajor: undefined,
  appVersionMiddle: undefined,
  appVersionMinor: undefined,
  nowDateTime: undefined,
  readerId: undefined,
  languageId: undefined,
};

const presets = [
  {
    name: 'Premium',
    groupState: undefined,
    nowDateTime: new Date().toISOString(),
    planIds: [1],
    platform: 1,
    appVersionMajor: 2023,
    appVersionMiddle: 11,
    appVersionMinor: 9,
    languageId: undefined,
  },
  {
    name: 'B2B',
    groupState: undefined,
    nowDateTime: new Date().toISOString(),
    planIds: [16],
    platform: 1,
    appVersionMajor: 2023,
    appVersionMiddle: 11,
    appVersionMinor: 9,
    languageId: undefined,
  },
  {
    name: 'Freemium',
    groupState: undefined,
    nowDateTime: new Date().toISOString(),
    planIds: [15],
    platform: 1,
    appVersionMajor: 2023,
    appVersionMiddle: 11,
    appVersionMinor: 9,
    languageId: undefined,
  },
];

const platformsValues = Object.values(Server.Platform).filter(
  (v) => typeof v === 'string'
) as (keyof typeof Server.Platform)[];

const platforms: { value: Server.Platform; label: string | undefined }[] =
  platformsValues.map((v) => {
    return {
      value: Server.Platform[v],
      label: v,
    };
  });

const userGroupsValues = Object.values(Server.UserGrouping).filter(
  (v) => typeof v === 'string'
) as (keyof typeof Server.UserGrouping)[];

const userGroups: {
  value: Server.UserGrouping;
  label: string | undefined;
}[] = userGroupsValues.map((v) => {
  return {
    value: Server.UserGrouping[v],
    label: v,
  };
});

export const PreviewFlowSelectionModal = ({
  isOpen,
  onClose,
  onSubmit,
}: PreviewFlowSelectionModalProps) => {
  const navigate = useNavigate();

  const contentDispatch = useContentDispatch();
  const productDispatch = useProductDispatch();

  const plansLookupsStatus = useProductSelector(({ plans }) => plans.status);
  const plansLookups = useProductSelector(({ plans }) => plans.data);

  const selectionFlow = useContentSelector(({ flows }) => flows.selectionFlow);

  const isSaveLoading = selectionFlow.status === 'loading';

  const languages = useGetLanguagesLookups();

  const { control, handleSubmit, setValue, reset } = useForm<FormInput>({
    defaultValues: formInputInitialValues,
  });

  const onFormSubmit: SubmitHandler<FormInput> = async (data) => {
    const mappedData: FormInput = {
      ...data,
      groupState: Number(data.groupState),
      platform: Number(data.platform),
      planIds: data.planIds?.map((p) => Number(p)),
    };

    for (const key in mappedData) {
      if (
        mappedData[key as keyof typeof mappedData] !== 0 &&
        !mappedData[key as keyof typeof mappedData]
      ) {
        mappedData[key as keyof typeof mappedData] = undefined;
      }
    }

    contentDispatch(FlowsActions.clearSelectionFlow());

    const res = await contentDispatch(
      FlowsActions.getSelectionFlowAsync(mappedData)
    );

    if (res.meta.requestStatus === 'fulfilled') {
      onSubmit && onSubmit();
    }
  };

  useEffect(() => {
    getLanguagesLookups();

    productDispatch(PlansActions.getAllPlansAsync());
  }, [contentDispatch]);

  const renderPresets = () => {
    return presets.map((p) => {
      return (
        <div className="w-[90px]">
          <ButtonHeadless
            bgStyle="bg-white"
            borderStyle="border border-primary"
            borderRadiusStyle="rounded-[6px]"
            paddingYStyle="py-[6px]"
            action={() => {
              reset(p);
            }}
          >
            <div>
              <ReturnTranslatedText
                text={`buttons.${p.name}`}
                origintext={p.name}
              />
            </div>
          </ButtonHeadless>
        </div>
      );
    });
  };

  return (
    <Modal
      title="Preview Flow Selection"
      withScroll={true}
      open={isOpen}
      onClose={onClose}
      handleAlert={false}
      alertTarget="modal"
      fontClassName="font-['Tajawal']"
      widthClassName="w-[950px] max-w-[950px]"
    >
      {plansLookupsStatus === 'loading' || languages.status === 'loading' ? (
        <div className="w-full min-h-[500px] flex justify-center items-center">
          <Spin />
        </div>
      ) : (
        <div className="w-full min-h-[500px]">
          <div className="w-full flex justify-start items-center">
            <div className="w-[150px] flex justify-center items-center">
              <InputLabel name="Saved Inputs" text="Saved Inputs" />
              <span>:</span>
            </div>
            <div className="w-full flex justify-start items-center space-s-2">
              {renderPresets()}
            </div>
          </div>
          <form
            onSubmit={handleSubmit(onFormSubmit)}
            className="mt-4 w-full h-full pb-4 flex flex-col justify-start items-start border-b font-['Tajawal']"
            noValidate
          >
            <div className="w-full grid grid-cols-4 gap-2">
              <div className="w-full">
                <FormInputController
                  control={control}
                  name={`groupState`}
                  fieldMeta={{
                    fieldType: Server.FieldType.Select,
                    fieldOptions: {},
                  }}
                  renderInput={({ ...props }) => (
                    <Autocomplete
                      id={`groupState`}
                      options={userGroups.map((g) => {
                        return {
                          value: g.value.toString(),
                          label: g.label,
                        };
                      })}
                      label="User Group"
                      color="#1f7983"
                      {...props}
                    />
                  )}
                />
              </div>
              <div className="w-full">
                <FormInputController
                  control={control}
                  name={`platform`}
                  fieldMeta={{
                    fieldType: Server.FieldType.Select,
                    fieldOptions: {},
                  }}
                  renderInput={({ ...props }) => (
                    <Autocomplete
                      id={`platform`}
                      options={platforms.map((p) => {
                        return {
                          value: p.value.toString(),
                          label: p.label,
                        };
                      })}
                      label="Platform"
                      color="#1f7983"
                      {...props}
                    />
                  )}
                />
              </div>
              <div className="w-full">
                <FormInputController
                  control={control}
                  name={`languageId`}
                  fieldMeta={{
                    fieldType: Server.FieldType.Select,
                    fieldOptions: {},
                  }}
                  renderInput={({ ...props }) => (
                    <Autocomplete
                      id={`languageId`}
                      options={languages.data.map((l) => {
                        return {
                          value: l.id?.toString(),
                          label: l.name,
                        };
                      })}
                      label="Language"
                      color="#1f7983"
                      {...props}
                    />
                  )}
                />
              </div>
              <WajeezFormInputV2
                control={control}
                name={`iSOThreeDigitCountryCode`}
                fieldMeta={{
                  fieldType: Server.FieldType.Text,
                  fieldOptions: {
                    maxLength: 2,
                    minLength: 2,
                  },
                  label: 'Country Code',
                  placeHolder: 'Country Code',
                  onChange: (value) => {
                    setValue('iSOThreeDigitCountryCode', value.toUpperCase());
                  },
                }}
                id={`country-code`}
                validationErrorMessages={{
                  maxLength: 'This field should consist of 2 letters',
                  minLength: 'This field should consist of 2 letters',
                }}
              />
              <WajeezFormInputV2
                control={control}
                name={`appVersionMajor`}
                fieldMeta={{
                  fieldType: Server.FieldType.Number,
                  fieldOptions: {},
                  label: 'Major Version',
                  placeHolder: 'Major Version',
                }}
                id={`major-version`}
              />
              <WajeezFormInputV2
                control={control}
                name={`appVersionMiddle`}
                fieldMeta={{
                  fieldType: Server.FieldType.Number,
                  fieldOptions: {},
                  label: 'Middle Version',
                  placeHolder: 'Middle Version',
                }}
                id={`middle-version`}
              />
              <WajeezFormInputV2
                control={control}
                name={`appVersionMinor`}
                fieldMeta={{
                  fieldType: Server.FieldType.Number,
                  fieldOptions: {},
                  label: 'Minor Version',
                  placeHolder: 'Minor Version',
                }}
                id={`minor-version`}
              />
              <div className="w-full">
                <WajeezFormInputV2
                  control={control}
                  name={`nowDateTime`}
                  fieldMeta={{
                    fieldType: Server.FieldType.DateTime,
                    fieldOptions: {},
                    label: 'Now Date Time',
                    placeHolder: 'Now Date Time',
                  }}
                  id={`now-date-time`}
                />
              </div>
              <WajeezFormInputV2
                control={control}
                name={`readerId`}
                fieldMeta={{
                  fieldType: Server.FieldType.Text,
                  fieldOptions: {},
                  label: 'Reader Id',
                  placeHolder: 'Reader Id',
                }}
                id={`reader-id`}
              />
              <div className="w-full col-span-3"></div>
              <div className="w-full col-span-2">
                <FormInputController
                  control={control}
                  name={`planIds`}
                  fieldMeta={{
                    fieldType: Server.FieldType.Select,
                    fieldOptions: {},
                  }}
                  renderInput={({ ...props }) => (
                    <Autocomplete
                      id={`planIds`}
                      options={plansLookups.map((p) => {
                        return {
                          value: p.id?.toString(),
                          label: p.referenceName,
                        };
                      })}
                      label="Plans"
                      color="#1f7983"
                      heightClassName="h-auto"
                      multiple
                      {...props}
                    />
                  )}
                />
              </div>
            </div>
            <div className="w-full pt-8 pb-4 flex justify-end items-center space-s-2">
              <div className="w-[90px]">
                <ButtonHeadless
                  borderRadiusStyle="rounded-[6px]"
                  paddingYStyle="py-[6px]"
                  bgStyle="bg-warmGray-300"
                  action={() => {
                    reset(formInputInitialValues);

                    contentDispatch(FlowsActions.clearSelectionFlow());
                  }}
                >
                  <div className="text-[15px] text-black font-medium">
                    <ReturnTranslatedText
                      text="buttons.Reset"
                      origintext="Reset"
                    />
                  </div>
                </ButtonHeadless>
              </div>
              <div className="w-[90px]">
                <ButtonHeadless
                  borderRadiusStyle="rounded-[6px]"
                  paddingYStyle="py-[6px]"
                  type="submit"
                  isDisabled={isSaveLoading}
                  bgStyle={
                    !isSaveLoading ? 'bg-primary' : 'bg-primary opacity-70'
                  }
                >
                  <div className="text-[15px] text-white font-medium flex justify-center items-center space-s-1">
                    <ReturnTranslatedText
                      text="buttons.Preview"
                      origintext="Preview"
                    />
                    {isSaveLoading && (
                      <BiLoaderAlt className="animate-spin w-5" />
                    )}
                  </div>
                </ButtonHeadless>
              </div>
            </div>
          </form>
          <div className="w-full pt-6">
            <div className="w-[400px] mx-auto p-4 shadow rounded-[8px] text-center">
              <div className="text-[23px] font-bold">
                <ReturnTranslatedText
                  text={`general.${'Flow Name'}`}
                  origintext="Flow Name"
                />
              </div>
              <div className="mt-4">
                {selectionFlow.data?.flowName ? (
                  <div className="flex justify-center items-center space-s-2">
                    <Link
                      to={`/flows/v2/${selectionFlow.data?.id}/pages/0`}
                      className="text-primary"
                    >
                      {selectionFlow.data?.flowName}
                    </Link>
                    <div
                      className="cursor-pointer"
                      onClick={() => {
                        navigate(`/flows/v2/${selectionFlow.data?.id}/edit`);
                      }}
                    >
                      <FiEdit2 />
                    </div>
                  </div>
                ) : (
                  <ReturnTranslatedText
                    text={`general.${'There is no data'}`}
                    origintext="There is no data"
                  />
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </Modal>
  );
};
